import aboutHtml from './about.html?raw';

export interface AboutOptions {
  /** Load a user-picked SoundFont file. Rejects (with a message) on a bad font. */
  onLoadFont?: (file: File) => Promise<void>;
  /** Restore the bundled default SoundFont. */
  onResetFont?: () => Promise<void>;
}

export interface AboutApi {
  open: () => void;
  close: () => void;
  /** Update the "Current: …" font label shown in the dialog. */
  setFontName: (name: string) => void;
}

export function installAbout(opts: AboutOptions = {}): AboutApi {
  const dialog = document.getElementById('about') as HTMLDialogElement | null;
  const content = document.getElementById('about-content');
  if (!dialog || !content) {
    return { open: () => {}, close: () => {}, setFontName: () => {} };
  }
  content.innerHTML = aboutHtml;
  const closeBtn = dialog.querySelector('.close') as HTMLButtonElement | null;
  closeBtn?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });

  // --- SoundFont controls ---
  const nameEl = content.querySelector('#sf-name') as HTMLElement | null;
  const statusEl = content.querySelector('#sf-status') as HTMLElement | null;
  const loadBtn = content.querySelector('#sf-load') as HTMLButtonElement | null;
  const resetBtn = content.querySelector('#sf-reset') as HTMLButtonElement | null;
  const fileInput = content.querySelector('#sf-input') as HTMLInputElement | null;

  const setStatus = (msg: string): void => {
    if (statusEl) statusEl.textContent = msg;
  };
  const setBusy = (busy: boolean): void => {
    if (loadBtn) loadBtn.disabled = busy;
    if (resetBtn) resetBtn.disabled = busy;
  };

  // Serialize load operations so a second pick can't race a font swap in flight.
  const run = async (label: string, fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setStatus(`${label}…`);
    try {
      await fn();
      setStatus('');
    } catch (err) {
      setStatus(err instanceof Error ? `Couldn't load: ${err.message}` : "Couldn't load font.");
    } finally {
      setBusy(false);
    }
  };

  loadBtn?.addEventListener('click', () => fileInput?.click());
  fileInput?.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    fileInput.value = ''; // allow re-picking the same file later
    if (!file || !opts.onLoadFont) return;
    void run('Loading', () => opts.onLoadFont!(file));
  });
  resetBtn?.addEventListener('click', () => {
    if (!opts.onResetFont) return;
    void run('Loading default', () => opts.onResetFont!());
  });

  return {
    open: () => {
      if (!dialog.open) dialog.showModal();
    },
    close: () => {
      if (dialog.open) dialog.close();
    },
    setFontName: (name: string) => {
      if (nameEl) nameEl.textContent = name;
    },
  };
}
